<template>
  <div class="subscription" :class="isMultiInstance ? 'multi-instance' : 'single-instance'">
    <template v-if="isMultiInstance">
      <div class="row multi-instance-header">
        <div class="col-l-12">
          <h4 :id="id">
            <slot name="name" />
          </h4>
          <dl class="sr">
            <slot name="minimumCommitmentPeriod" v-bind="instances[0]" />
          </dl>
        </div>
      </div>
      <div class="row buttons" style="padding-top: 0">
        <div class="col-l-12 text-left">
          <slot name="buttons" v-bind="instances[0]" />
        </div>
      </div>
      <slot name="confirm" />
    </template>

    <template v-for="instance in instances">
      <div :key="instance[idKey]" class="instance" :class="isMultiInstance ? 'multi' : 'single'" :data-test="'instance_' + instance[idKey]">
        <div class="row first">
          <div class="col-l-4 col-m-9 col-s-7 col-xs-8">
            <h5 v-if="isMultiInstance">
              <slot name="sub-name" v-bind="instance" />
            </h5>
            <h4 v-else><slot name="name" /></h4>
          </div>
          <div :class="hasAnyPromo ? 'col-l-3' : 'col-l-6'" class="col-m-hide col-s-hide col-xs-hide" aria-hidden="true">
            <slot name="bookingPeriod" v-bind="instance" />
          </div>
          <div v-if="hasAnyPromo" class="col-l-3 col-m-hide col-s-hide col-xs-hide" aria-hidden="true">
            <slot v-if="instance[promoKey]" name="promotionPeriod" v-bind="instance" />
          </div>
          <div class="col-l-1 col-m-1 col-s-2 col-xs-hide" aria-hidden="true">
            <slot name="state" v-bind="instance" />
          </div>
          <div class="col-l-1 col-m-2 col-s-3 col-xs-4 text-right">
            <a
              role="button"
              href="#"
              class="text-nowrap"
              :aria-expanded="isExpanded(instance[idKey]) ? 'true' : 'false'"
              :aria-describedby="id"
              :aria-label="$t('common.product_details_toggle_aria_label_text', { productName: name, instance: instance[idKey] })"
              @click.prevent="onToggle(instance[idKey])"
            >
              <FontAwesomeIcon :icon="isExpanded(instance[idKey]) ? faCaretUp : faCaretDown" />
              {{ $t('common.product_details_toggle_label') }}
              <!-- #S827 -->
            </a>
          </div>
        </div>
        <div class="row second">
          <div class="col-l-12">
            <small class="promotion-type">
              <slot name="small" v-bind="instance" />
            </small>
            <dl class="sr">
              <template v-if="!isMultiInstance">
                <slot name="minimumCommitmentPeriod" v-bind="instance" />
              </template>

              <dt class="col-xl-sr-only col-l-sr-only">
                {{ $t('common.booking_period_headline') }}
                <!-- #S819 -->
              </dt>
              <dd class="col-xl-sr-only col-l-sr-only"><slot name="bookingPeriod" v-bind="instance" /></dd>

              <template v-if="instance[promoKey]">
                <dt class="col-xl-sr-only col-l-sr-only">
                  {{ $t('common.promotion_period_headline') }}
                  <!-- #S820 -->
                </dt>
                <dd class="col-xl-sr-only col-l-sr-only"><slot name="promotionPeriod" v-bind="instance" /></dd>
              </template>

              <dt class="col-xl-sr-only col-l-sr-only col-m-sr-only col-s-sr-only">
                {{ $t('common.status_headline') }}
                <!-- #S821 -->
              </dt>
              <dd class="col-xl-sr-only col-l-sr-only col-m-sr-only col-s-sr-only"><slot name="state" v-bind="instance" /></dd>
            </dl>
          </div>
        </div>
        <div class="important">
          <slot name="important" v-bind="instance" />
        </div>

        <template v-if="!isMultiInstance">
          <div class="row buttons" style="padding-top: 0">
            <div class="col-l-12 text-left">
              <slot name="buttons" v-bind="instance" />
            </div>
          </div>
          <slot name="confirm" />
        </template>

        <transition name="slide-fade2" appear>
          <div v-if="isExpanded(instance[idKey])" :key="'details' + instance[idKey]" data-test="details">
            <slot name="details" v-bind="instance" />
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';

export default {
  name: 'StockGridItemLayout',

  components: {
    FontAwesomeIcon,
  },

  props: {
    instances: {
      type: Array,
      required: true,
    },

    hasAnyPromo: {
      type: Boolean,
      required: false,
    },

    idKey: {
      type: String,
      required: false,
      default: 'id',
    },

    promoKey: {
      type: String,
      required: false,
      default: 'promotionEnd',
    },

    initialDetailsExpanded: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    name: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      detailsExpanded: this.initialDetailsExpanded,
      faCaretUp,
      faCaretDown,
      id: Math.random().toString(36).substring(2),
    };
  },

  computed: {
    isMultiInstance() {
      return this.instances.length > 1;
    },
  },

  methods: {
    isExpanded(id) {
      return this.detailsExpanded[id];
    },

    onToggle(id) {
      this.$set(this.detailsExpanded, id, !this.detailsExpanded[id]);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.subscription {
  padding-top: 12px;
  padding-bottom: 12px;
  margin-right: -12px;
  margin-bottom: 24px;
  margin-left: -12px;
  background-color: #fff;
  border: 1px solid #eee;

  h4,
  h5 {
    padding: 0 0 6px;
    margin: 0;
  }

  > .row,
  .instance .row.first,
  .instance .row.second,
  .instance .row.buttons {
    padding: 12px 0;
    margin: 0 12px;
  }

  .instance .row.first {
    padding-bottom: 0;
  }

  .instance .row.second {
    padding-top: 0;
  }

  .multi.instance {
    border-top: 1px solid $color-border-bright;
  }
}

.subscription ::v-deep {
  .row.confirmation {
    display: flex;
    padding: 12px;
    margin: 0 12px;
  }

  .row.confirmation > div {
    width: 100%;
  }

  .instance.single .row.confirmation {
    padding-bottom: 0;
  }

  .text-center .btn {
    margin-right: 6px;
    margin-left: 6px;
  }

  .text-right .btn {
    margin-left: 12px;
  }

  .text-left .btn {
    margin-right: 12px;
  }

  .block-infofield {
    margin-bottom: 12px;
    border-bottom: 1px solid $color-border-bright;
  }
  .important .block-infofield {
    border-bottom: none;
  }
  .block-infofield:last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }

  .infofield-img {
    float: left;
  }
}

@media screen and (max-width: #{$grid-medium-min}px) {
  .subscription ::v-deep {
    .text-left .btn,
    .text-right .btn,
    .text-center .btn {
      width: 100%;
      margin-right: 0;
      margin-bottom: 12px;
      margin-left: 0;
    }

    .infofield-img {
      display: block;
      float: none;
    }
  }
}
</style>
